import { Layout } from '@/features/layout/types'
import { actionsStylesheet } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/styles'
import { PaymentType } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/types'
import Chip from '@/features/ui/components/Chip'
import Collapsible from '@/features/ui/components/Collapsible'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { useStyles } from 'react-native-unistyles'

interface PaymentTypeChipsProps {
    layout: Layout
    paymentTypes: PaymentType[]
    selectedPaymentType: PaymentType | null
    disabled?: boolean
    onSelect: (paymentType: PaymentType) => void
}

const PaymentTypeChips: FC<PaymentTypeChipsProps> = ({
    layout,
    paymentTypes,
    selectedPaymentType,
    disabled,
    onSelect,
}) => {
    const [showMore, setShowMore] = useState(false)
    const rowSize = 3

    const { t } = useTranslation()

    const { styles } = useStyles(actionsStylesheet, { layout })

    const primaryPaymentTypes = paymentTypes.slice(0, 2)

    const extraPaymentTypes = useMemo(() => {
        return paymentTypes.slice(2)
    }, [paymentTypes])
    const hasExtraPaymentTypes = extraPaymentTypes.length > 0
    const extraPaymentTypesSignature = extraPaymentTypes.join('|')

    const toggleLabel = showMore
        ? t('pos.orderFinalization.actions.less')
        : t('pos.orderFinalization.actions.more')

    const extraPaymentTypeRows = useMemo(() => {
        if (!showMore) return []

        const rows: (PaymentType | null)[][] = []
        const remainder = extraPaymentTypes.length % rowSize
        let index = 0

        if (remainder) {
            rows.push([
                ...new Array<PaymentType | null>(rowSize - remainder).fill(null),
                ...extraPaymentTypes.slice(0, remainder),
            ])
            index = remainder
        }

        for (; index < extraPaymentTypes.length; index += rowSize) {
            rows.push(extraPaymentTypes.slice(index, index + rowSize))
        }

        return rows
    }, [extraPaymentTypes, showMore])

    const renderPaymentTypeButton = (paymentType: PaymentType) => (
        <Chip
            key={paymentType}
            active={selectedPaymentType === paymentType}
            disabled={disabled}
            label={t(`orders.paymentType.${paymentType}`)}
            onPress={() => onSelect(paymentType)}
            style={styles.paymentTypeButton}
            labelStyle={styles.paymentTypeLabel}
        />
    )

    useEffect(() => {
        if (!extraPaymentTypes.length) {
            setShowMore(false)
            return
        }

        if (selectedPaymentType && extraPaymentTypes.includes(selectedPaymentType)) {
            setShowMore(true)
        }
    }, [extraPaymentTypes, selectedPaymentType])

    return (
        <>
            <Collapsible
                key={extraPaymentTypesSignature}
                open={showMore && hasExtraPaymentTypes}
                containerStyle={styles.extraPaymentTypesContainer}
            >
                <View style={styles.extraPaymentTypesContent}>
                    {extraPaymentTypeRows.map((row, index) => (
                        <View key={index} style={styles.paymentTypesRow}>
                            {row.map((paymentType, cellIndex) =>
                                paymentType ? (
                                    renderPaymentTypeButton(paymentType)
                                ) : (
                                    <View
                                        key={`empty-${index}-${cellIndex}`}
                                        style={styles.paymentTypeButton}
                                    />
                                )
                            )}
                        </View>
                    ))}
                </View>
            </Collapsible>
            <View style={styles.paymentTypesRow}>
                {primaryPaymentTypes.map((paymentType) => renderPaymentTypeButton(paymentType))}
                {hasExtraPaymentTypes && (
                    <Chip
                        active={false}
                        disabled={disabled}
                        label={toggleLabel}
                        onPress={() => setShowMore((prev) => !prev)}
                        style={styles.paymentTypeButton}
                        labelStyle={styles.paymentTypeLabel}
                    />
                )}
            </View>
        </>
    )
}

export default PaymentTypeChips
